import { useAuth } from "contexts/authContext";
import { IOccurrence } from "interfaces/Occurrence";
import { LatLngTuple } from "leaflet";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getOccurrencesIdService } from "services/OccurrenceService";
import { DashboardTemplate } from "templates/DashboardTemplate";
import { PolicemanList, TenantList } from "utils/treatSocketLocations";
import OccurrenceMap from "./Map";
import SideBar from "./SideBar";
import * as L from "leaflet";

import * as S from "./styles";
import { AdaptedLocation } from "adapters/LocationsAdapter";
import LocationsService, {
  GetLocationsParams,
} from "services/LocationsService";
import { socket } from "hooks/useSocket";
import { fireToast } from "services/sweetAlertConfig";

export default function OccurrenceMapPage() {
  const [occurrenceLocation, setOccurrenceLocation] = useState<LatLngTuple>();
  const [occurrence, setOccurrence] = useState<IOccurrence | null>(null);
  const [policemanLocation, setPolicemanLocation] = useState<LatLngTuple>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const mapRef = useRef<L.Map>(null);

  const [polyline, setPolyline] = useState<L.Polyline | null>(null);
  const [polylineLocations, setPolylineLocations] = useState<AdaptedLocation>(
    []
  );
  const { occurrenceId } = useParams<{ occurrenceId: string }>();
  const { tenant } = useAuth();

  async function fetchPolylineCoords(params: GetLocationsParams) {
    try {
      const polylineCoords = await LocationsService.getLocations(params);
      setPolylineLocations(polylineCoords);
    } catch {
      fireToast(
        "ATENÇÃO! O apoio ainda nao enviou nenhuma coordenada, portanto nao sera possivel visualizar o trajeto.",
        4000,
        "top-right"
      );
    }
  }

  useEffect(() => {
    if (occurrence && occurrence.report) {
      const params: GetLocationsParams = {
        policemanId:
          occurrence?.policemen?.[0]?.policeman?.id,
        reportId: occurrence.report?.[0]?.id,
      };
      fetchPolylineCoords(params);
    }
  }, [occurrence]);

  // function to fetch the occurrence data
  async function fetchOccurrence() {
    try {
      const response = await getOccurrencesIdService(occurrenceId);

      if (response?.item) {
        const occurrenceCoords: LatLngTuple = [
          Number(response.item.lastCoordX),
          Number(response.item.lastCoordY),
        ];

        setOccurrence(response.item);
        setOccurrenceLocation(occurrenceCoords);

        mapRef.current?.flyTo(occurrenceCoords);
      }
    } catch (error: any) {
      setErrorMessage("Ocorreu um erro, tente novamente mais tarde.");
    }
  }

  // function to adapt the policemanLocation from the occurrence object
  function getPolicemanLocation(objList: PolicemanList) {
    const policemanId =
      occurrence?.policemen?.[0]?.policeman?.id;
    const isDetachedPoliceman = occurrence?.report?.[0]?.superficialPoliceman
    
    if (isDetachedPoliceman) {
      fireToast(
        'O policial associado a essa ocorrência é avulso. Não será possível localizá-lo no mapa.'
      )
      
      return;
    }

    if (policemanId && objList[policemanId]?.coords) {
      setPolicemanLocation([
        objList[policemanId].coords.latitude,
        objList[policemanId].coords.longitude,
      ]);
    }
  }
  
  // function to listen to the socket and set the policemanLocation in real time
  function handleSocketLocationChange(locations: TenantList | PolicemanList) {
    const tenantId = tenant?.id;
    if (!tenantId) return;

    if (tenantId in locations) {
      getPolicemanLocation(locations[tenantId] as PolicemanList);
    } else {
      getPolicemanLocation(locations as PolicemanList);
    }
  }
  // useEffect to trigger the onLocationChange and listLocations keys
  useEffect(() => {
    socket.on("onLocationChange", handleSocketLocationChange);

    if (tenant?.id) {
      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", handleSocketLocationChange);
    }

    return () => {
      socket.off("onLocationChange");
      socket.off("listLocations");
    };
  }, [occurrence, tenant]); // eslint-disable-line

  // useEffect to fetch the occurrence data on mount
  useEffect(() => {
    fetchOccurrence();
  }, []);

  return (
    <S.Container>
      <DashboardTemplate containerClass="wrapper" hasTableWrapper>
        <S.StyledHeader title="Mapa da ocorrencia" />

        <S.ContentWrapper>
          <SideBar
            map={mapRef}
            occurrence={occurrence}
            occurrenceLocation={occurrenceLocation}
            policemanLocation={policemanLocation}
            showPolicemanCoordinates={() => {
              setPolyline((prev) => {
                if (!prev) {
                  const mapPolyline = L.polyline(polylineLocations, {
                    color: "#191919",
                  }).addTo(mapRef.current!);

                  return mapPolyline;
                } else {
                  mapRef.current!.removeLayer(prev);
                  return null;
                }
              });
            }}
          />

          <OccurrenceMap
            map={mapRef}
            occurrence={occurrence}
            occurrenceLocation={occurrenceLocation}
            policemanLocation={policemanLocation}
          />
        </S.ContentWrapper>
      </DashboardTemplate>
    </S.Container>
  );
}
